import { useState, useEffect } from "react";
// Tools
import { no_propagate } from "./utils";

// A small (i) beside a label, revealing a line of explanation. A mouse only has
// to hover it; a touchscreen has no hover, so a tap pins the bubble open until
// it is tapped again.
//
// Returns the icon and the bubble as siblings rather than wrapping them, so the
// bubble is positioned against the whole label it is dropped into. That keeps it
// centred and inside the panel: anchoring it to the icon instead would let a
// long line run off the side of a narrow screen.
//
// `visible` is whether the panel this sits in is on show, so that a bubble left
// pinned is not still there the next time that panel is opened.
const InfoTooltip = ({ text, visible = true }) => {

  // The two ways in are tracked separately: unpinning with the mouse still over
  // the icon should leave the bubble up rather than blink it away.
  const [hovered, set_hovered] = useState(false);
  const [pinned, set_pinned] = useState(false);

  useEffect(() => {
    if (!visible) set_pinned(false);
  }, [visible]);

  // While the bubble is pinned, the next click anywhere is spent dismissing it
  // and does nothing else - otherwise it would reach the panel underneath,
  // whose own handler closes the entire menu.
  //
  // The listener is on the document and captures, so it runs ahead of React's
  // listeners at the app root and can stop the click ever reaching them. The
  // click that pinned the bubble is safe: the document's capture phase is long
  // past by the time its handler adds this.
  useEffect(() => {
    if (!pinned) return;
    const dismiss = (e) => {
      e.stopPropagation();
      set_pinned(false);
    };
    document.addEventListener("click", dismiss, true);
    return () => document.removeEventListener("click", dismiss, true);
  }, [pinned]);

  // Touching an element raises mouse events too, which would count a tap as a
  // hover that never ends, leaving the bubble impossible to dismiss.
  const on_pointer = (is_over) => (e) => {
    if (e.pointerType === "mouse") set_hovered(is_over);
  };

  return (
    <>
      <button className="info_tooltip_button"
              onPointerEnter={on_pointer(true)}
              onPointerLeave={on_pointer(false)}
              onClick={no_propagate(() => set_pinned(!pinned))}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="7" x2="12" y2="7" />
          <line x1="12" y1="11" x2="12" y2="17" />
        </svg>
      </button>
      {(hovered || pinned) && <span className="info_tooltip_bubble">{text}</span>}
    </>
  );
};

export default InfoTooltip;
